import React from 'react';
import { InputRef } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import AntFormItem from 'antd/lib/form/FormItem';
import { BoxNumber } from 'components/ui/base/box-number/box-number.styles';
import NumberInput from 'components/ui/base/input/NumberInput';
import Input from 'components/ui/base/input/Input';
import { Text, TextSmallBold } from 'components/ui/base/typography';
import styled, { css } from 'styled-components';
import { colors, fonts } from 'theme';

interface ErrorProp {
    error: boolean;
    touched: boolean;
    condensed?: boolean;
}

export type LabelSpacing = 'none' | 'small' | 'medium';

const labelSpacingMap: Record<LabelSpacing, string> = {
    none: '0px',
    small: '2px',
    medium: '6px',
};

export const LabelDiv = styled.div<{ condensed?: boolean; $labelSpacing?: LabelSpacing }>`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    height: 100%;
    margin-bottom: ${({ condensed }) => (condensed ? 0 : '0.2rem')};
    padding-bottom: ${({ $labelSpacing = 'none' }) => labelSpacingMap[$labelSpacing]};
`;

export const RefDiv = styled(BoxNumber)`
    margin-right: 0.8rem;
    height: min-content;
    background-color: ${colors.offWhite};
    color: ${colors.grey8};
    white-space: nowrap;
    width: min-content;
    padding: 0px 6px;
`;

export const InputLabel = styled.label`
    color: ${colors.grey9};
    font-weight: ${fonts.weight.regular};
    font-size: 1.4rem;
    line-height: 2.2rem;
    display: inline-block;
`;

export const InputLabelRequired = styled.span<{
    right?: boolean;
}>`
    color: ${colors.error};
    font-weight: ${fonts.weight.semibold};
    font-size: 1.4rem;
    line-height: 2.2rem;
    ${({ right }) => (right ? 'margin-right: 0.8rem' : 'margin-left: 0.8rem')};
`;

export const ErrorLabel = styled.label`
    color: ${colors.error};
    font-weight: ${fonts.weight.regular};
    font-size: 1.4rem;
`;

type StyledInputProps = {
    showSuccess: boolean;
    condensed?: boolean;
    inputRef?: React.Ref<HTMLInputElement>;
    lock?: boolean;
} & React.ComponentProps<typeof Input>;

const RawStyledInput = styled(Input)<StyledInputProps>`
    border-radius: 6px;
    ${({ lock }) => lock && 'border-top-right-radius: 0px !important; border-bottom-right-radius: 0px !important;'}
    border: ${({ condensed }) => (condensed ? '1px solid #cecece' : 'hsv(0, 0, 85%)')};
`;
export const StyledInput = React.forwardRef<InputRef, StyledInputProps>(({ className, ...props }, ref) => {
    return <RawStyledInput {...props} ref={ref} className={className} />;
});

StyledInput.displayName = 'StyledInput';
export const FormItem = styled(AntFormItem)`
    width: 100%;
    margin-bottom: 0;
    display: flex;
    align-items: center;
    & .ant-form-item-row {
        flex-grow: 1;
    }
`;

export const StyledNumberInput = styled(NumberInput)<{ condensed?: boolean; lock?: boolean }>`
    width: 100%;
    border-radius: ${({ condensed }) => (condensed ? '6px' : '2px')};
    border: ${({ condensed }) => (condensed ? '1px solid #cecece' : 'hsv(0, 0, 85%)')};
    ${({ lock }) => lock && 'border-top-right-radius: 0px !important; border-bottom-right-radius: 0px !important;'}
`;

export const InputDiv = styled.div`
    max-width: 100%;
    width: 100%;
    display: flex;
`;

export const ErrorDiv = styled.div<ErrorProp>`
    width: 100%;
    margin-bottom: ${({ error, touched, condensed }) => (error && touched ? '0' : condensed ? '0' : '2.2rem')};
`;

export const FlexDiv = styled.div`
    width: 100%;
    display: flex;
`;

export const ViewOnlyDiv = styled.div`
    display: flex;
    margin-bottom: 0.8rem;
`;

export const ViewOnlyLabel = styled(TextSmallBold)`
    margin-right: 0.5rem;
`;

export const SelectAccountNumbers = styled(Text)`
    cursor: pointer;
`;

export const SuccessDiv = styled.div`
    width: 100%;
    margin-bottom: 1.1rem;
`;

export const SuccessIcon = styled(CheckCircleFilled)`
    color: ${colors.success};
`;

export const FormatDiv = styled(BoxNumber)`
    margin-left: 0.8rem;
    white-space: nowrap;
    width: auto;
    padding: 2.5px 5px;
    background-color: rgba(56, 55, 82, 0.2);
`;

export const MetaSection = styled.div`
    flex-shrink: 0;
    padding-block: 5px;
    width: 40%;
`;

export const InputSection = styled.div`
    flex: 1;
    min-width: 0;
    max-width: 100%;
    width: 100%;
`;

const columnLayout = css`
    flex-direction: column;
    justify-content: flex-start;
    gap: 4px;

    ${MetaSection} {
        width: 100%;
        padding-block: 0;
    }
`;

// Handle the different layots for declaration form elements
// based on the context they are rendered and the screen size
export const FieldWrapper = styled.div<{ $viewOnly: boolean | undefined; $template: boolean | undefined }>`
    display: flex;
    flex-direction: row;
    width: 100%;
    justify-content: space-between;
    gap: 16px;

    ${({ $viewOnly, $template }) =>
        !$viewOnly &&
        !$template &&
        css`
            @media (max-width: 1024px) {
                ${columnLayout}
            }

            @media (min-width: 1600px) {
                ${columnLayout}
            }
        `}

    // Override the media queries layout for declaration form elements
    // when we're using them outside of the form as in the box44 forms
    .fixed-row-layout & {
        flex-direction: row;
        justify-content: space-between;
        gap: 16px;

        ${MetaSection} {
            width: 40%;
            padding-block: 5px;
        }
    }
`;
